import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * يؤخّر تطبيق القيمة حتى يتوقف الإدخال.
 *
 * بدونه يُطلق كل حرف في مربع البحث طلبًا إلى الخادم، فتتزاحم النتائج وقد
 * تصل استجابة قديمة بعد أحدث منها فتُظهر بيانات لا تطابق ما كُتب.
 */
export function useDebounced<T>(value: T, delayMs = 350): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}

/**
 * حالة القائمة محفوظة في رابط الصفحة.
 *
 * الفائدة أن الصفحة والبحث والتصفية تبقى عند التحديث وتُشارَك بنسخ الرابط،
 * وأن زر الرجوع في المتصفح يعمل كما يتوقع المستخدم.
 */
export function useListState(defaults: Record<string, string> = {}) {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const out: Record<string, string> = { page: '1', ...defaults };
    params.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }, [params, defaults]);

  function patch(changes: Record<string, string | undefined>): void {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    // أي تغيير في التصفية يعيد إلى الصفحة الأولى، وإلا ظهرت صفحة فارغة.
    if (!('page' in changes)) next.delete('page');
    setParams(next, { replace: true });
  }

  return { state, patch, page: Number(state.page ?? 1) || 1 };
}

/**
 * رقم يعدّ صعودًا عند تغيّر قيمته.
 *
 * الحركة هنا ليست زينة: العين تلتقط ما يتحرك، والعدّ يجعل القارئ ينظر إلى
 * الرقم بدل أن يمرّ عليه. وتقع مرة عند تغيّر القيمة لا في كل تصيير، وإلا
 * ارتجفت الشاشة كلما أُعيد جلب البيانات.
 */
export function useCountUp(target: number, duration = 650): number {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  const frame = useRef(0);

  useEffect(() => {
    const snap = () => {
      setValue(target);
      from.current = target;
    };

    /*
     * الرقم يُعرض فورًا في ثلاث حالات.
     *
     * تقليل الحركة تفضيلٌ يضبطه المستخدم في نظامه لأن الحركة تسبّب دوارًا
     * لبعض الناس. والتبويب المخفيّ لا تعمل فيه requestAnimationFrame أصلًا،
     * فبلا هذا الفحص يبقى الرقم على قيمته الأولى — وقع هذا فعلًا: رقمٌ صفرٌ
     * والنسبة تحته ثلاثون بالمئة، وهو أسوأ من غياب الحركة لأنه رقم خاطئ
     * معروض بثقة.
     */
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || document.visibilityState === 'hidden' || from.current === target) {
      snap();
      return;
    }

    const start = performance.now();
    const origin = from.current;
    const delta = target - origin;

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      /* تباطؤ في النهاية: الحركة الخطية تبدو آلية. */
      const eased = 1 - (1 - t) ** 3;
      setValue(Math.round(origin + delta * eased));
      if (t < 1) frame.current = requestAnimationFrame(step);
      else from.current = target;
    };

    frame.current = requestAnimationFrame(step);

    /*
     * حبل نجاة إن توقّفت الإطارات في المنتصف.
     *
     * المتصفح يخنق الإطارات حين يُخفى التبويب ولو بعد بدء الحركة، فتتجمّد
     * القيمة عند نقطة عشوائية. هذا يضمن ظهور الرقم الصحيح مهما جرى.
     */
    const safety = window.setTimeout(snap, duration + 120);

    return () => {
      cancelAnimationFrame(frame.current);
      window.clearTimeout(safety);
    };
  }, [target, duration]);

  return value;
}
